import React, { FC } from 'react';
import { usePlayer } from 'providers/player/PlayerContext';
import { getMiniPlayerProps } from 'utils/fnData';
import { PlayingAssetType } from 'types/Player';
import { useLocation, useNavigate } from 'react-router';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import { MovieCard } from 'types/Asset';
import { getVodMovieDetailsURL, getVodPlayerURL } from 'utils/fnUrl';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { VODAssetEntitlementState } from 'types/Entitlement';
import { useAuth } from 'providers/useAuth/AuthContext';
import { StandardCover } from 'types/Cover';
import { CardContext, ImageAspectRatio, ModuleType } from 'types/Common';
import { ProgressIndicator } from 'components/ProgressIndicator/ProgressIndicator';
import { CardTrackingData } from 'types/Tracking';
import { getVodContentMarker } from 'utils/fnContentMarker';
import { useApp } from 'providers/useApp/AppContext';
import { getBookmark } from 'utils/playbackPosition';
import useYouboraSearchTracking from 'hooks/useYouboraSearchTracking';
import { StaticPageRoutes } from 'types/RouteTypes';
import { useSearch } from 'providers/useSearch/SearchContext';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';

const MovieCardAdapter: FC<{
    cover: StandardCover;
    type: ModuleType;
    cardContext: CardContext;
    trackingData: CardTrackingData;
    onMouseEnter?: (event: React.MouseEvent) => void;
    onMouseLeave?: (event: React.MouseEvent) => void;
    onClick?: (event: React.MouseEvent) => void;
}> = ({ cover, cardContext, type, trackingData, onMouseEnter, onMouseLeave, onClick }) => {
    const cardProps = {
        asset: cover,
        ...trackingData,
        onMouseEnter,
        onMouseLeave,
        onClick: (e: React.MouseEvent) => {
            if (onClick) {
                onClick(e);
            }
        },
    };

    if (cardContext === CardContext.COLLECTION) {
        const collectionVariant = type === ModuleType.MIXED ? CardVariant.LANDSCAPE : CardVariant.PORTRAIT;

        return type === ModuleType.MIXED ? (
            <Card variant={collectionVariant} context={cardContext} asset={cover} key={cover.uuid} {...trackingData} />
        ) : (
            <Card variant={collectionVariant} context={cardContext} {...cardProps} />
        );
    }

    const isLandscape = cover.landscape && type !== ModuleType.VOD_MOVIE && cover.aspectRatio !== ImageAspectRatio.PORTRAIT;

    return isLandscape ? (
        <Card variant={CardVariant.LANDSCAPE} context={cardContext} asset={cover} {...trackingData} />
    ) : (
        <Card variant={CardVariant.PORTRAIT} context={cardContext} {...cardProps} />
    );
};

export const MovieCardPresenter: FC<{
    asset: MovieCard;
    type: ModuleType;
    aspectRatio?: ImageAspectRatio;
    cardContext: CardContext;
    trackingData: CardTrackingData;
}> = ({ asset, type, aspectRatio, cardContext, trackingData }) => {
    const {
        id,
        title,
        price,
        vodAssetState,
        duration,
        isPortrait,
        entitlementEnd,
        watchPeriod,
        createdAt,
        countries,
        releaseYear,
        genres,
    } = asset;

    const metaData = [
        countries?.length ? countries.slice(0, 2).join(', ') : '',
        releaseYear || '',
        genres?.length ? genres.slice(0, 1).join(', ') : '',
    ]
        .filter(Boolean)
        .join(' ');
    const bookmark = getBookmark(asset.id, 'vod');
    const locationParamsPath = useDetailLocationParams();
    const { isGuest } = useAuth();
    const { appLanguage } = useApp();
    const navigate = useNavigate();
    const { isMini, updateMiniPlayerProps } = usePlayer();
    const image = asset?.image || asset?.image_url;

    const { searchTerm } = useSearch();
    const location = useLocation();
    const isSearchPage = location.pathname === StaticPageRoutes.SEARCH;

    const { handleContentFocus, handleContentFocusOut, handleContentClick, trackContentPlay } = useYouboraContentTracking();

    const { trackSearchClick } = useYouboraSearchTracking();

    const onPlayClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();

        trackContentPlay(asset.id, {
            contentLink: asset.id,
            contentLinkType: 'contentId',
        });

        const { movieGroupId } = asset as any;
        if (movieGroupId) {
            trackContentPlay('null', {
                contentLink: movieGroupId,
                contentLinkType: 'movieGroupId',
            });
        }

        if (isMini) {
            updateMiniPlayerProps(getMiniPlayerProps(asset, PlayingAssetType.VOD));
        } else {
            navigate(getVodPlayerURL(asset.id));
        }
    };

    const progress = (bookmark * 100) / duration;

    const getYouboraParams = (): ContentTrackingParams => ({
        section: trackingData['data-module-title'] || '',
        sectionOrder: trackingData['data-stripe-position'] || 1,
        column: trackingData['data-card-position'] || 1,
        row: 1,
        contentId: asset.id,
        contentLink: asset.id,
        contentLinkType: 'contentId',
    });

    const handleCardClick = () => {
        handleContentClick(getYouboraParams());

        if (isSearchPage && searchTerm) {
            const params = getYouboraParams();
            trackSearchClick({
                section: params.section,
                sectionOrder: params.sectionOrder,
                column: params.column,
                row: params.row,
                contentId: params.contentId,
                query: searchTerm,
                contentLink: params.contentLink,
                contentLinkType: params.contentLinkType,
            });
        }
    };

    const contentTrackingProps = {
        onMouseEnter: () => handleContentFocus(getYouboraParams()),
        onMouseLeave: handleContentFocusOut,
        onClick: handleCardClick,
    };

    const hasProgress = (): boolean => progress >= 0.02 && progress <= 98;

    const isRental = typeof watchPeriod !== 'undefined';

    const calculateExpirationTime = () => {
        if (isRental && createdAt && watchPeriod) {
            return createdAt + watchPeriod;
        }
        return undefined;
    };

    const expirationTimestamp = calculateExpirationTime();
    const effectiveEntitlementEnd = isRental ? expirationTimestamp : entitlementEnd;

    return (
        <MovieCardAdapter
            cover={{
                uuid: asset.id,
                id,
                title,
                image,
                aspectRatio,
                landscapeImage: image,
                path: getVodMovieDetailsURL(asset as any, locationParamsPath, appLanguage),
                metaData,
                circle: false,
                landscape: !isPortrait,
                assetType: asset.type,
                rawData: asset,
                renderPlayIcon:
                    !isGuest && (vodAssetState === VODAssetEntitlementState.ENTITLED || isRental || (asset.isWatchable ?? false)),
                progressBar: <ProgressIndicator percentage={hasProgress() && !isGuest ? progress : 0} />,
                onPlayIconClicked: onPlayClicked,
                contentMarker: getVodContentMarker(price, vodAssetState, effectiveEntitlementEnd),
            }}
            type={type}
            cardContext={cardContext}
            trackingData={trackingData}
            {...contentTrackingProps}
        />
    );
};
